"use client";

import { useEffect, useId, useState } from "react";
import Link from "next/link";
import { ArrowRight, Icon, iconPaths } from "@/components/site/Icons";

export type HomeService = {
  n: string;
  title: string;
  summary: string;
  points: string[];
  start: string;
  cta: string;
  ctaHref: string;
  more: string;
  href: string;
};

const ROTATE_MS = 7000;

/** Services tabs with a 7s autoplay that stops once a visitor picks a tab. */
export default function ServicesTabs({ services }: { services: HomeService[] }) {
  const [active, setActive] = useState(0);
  const [pinned, setPinned] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const baseId = useId();

  useEffect(() => {
    try {
      setReduceMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch {
      // matchMedia unavailable; keep autoplay.
    }
  }, []);

  useEffect(() => {
    if (pinned || reduceMotion) return;
    const timer = setInterval(() => setActive((i) => (i + 1) % services.length), ROTATE_MS);
    return () => clearInterval(timer);
  }, [pinned, reduceMotion, services.length]);

  const autoplay = !pinned && !reduceMotion;
  const current = services[active];

  return (
    <div className="reveal row items-stretch" style={{ gap: 32 }}>
      <div role="tablist" aria-label="Services" className="stack g8" style={{ flex: "1 1 320px" }}>
        {services.map((s, i) => {
          const on = i === active;
          return (
            <button
              key={s.n}
              id={`${baseId}-tab-${i}`}
              type="button"
              role="tab"
              aria-selected={on}
              aria-controls={`${baseId}-panel`}
              className={on ? "svc-tab on" : "svc-tab"}
              onClick={() => {
                setActive(i);
                setPinned(true);
              }}
            >
              <span className="mono" style={{ fontSize: 12, color: "#94a3b8", width: 22 }}>
                {s.n}
              </span>
              <span className="display" style={{ fontWeight: 700, fontSize: 20, lineHeight: "26px", flex: 1 }}>
                {s.title}
              </span>
              <Icon size={18} d={iconPaths.chevronRight} />
              {on && autoplay ? <span key={active} className="tab-progress" aria-hidden="true" /> : null}
            </button>
          );
        })}
      </div>
      <div style={{ flex: "999 1 520px", minWidth: 0 }}>
        <div
          key={active}
          id={`${baseId}-panel`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${active}`}
          className="panel solid panel-in stack g20"
          style={{ height: "100%", position: "relative", overflow: "hidden", padding: 40 }}
        >
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              right: -60,
              top: -60,
              width: 260,
              height: 260,
              borderRadius: "50%",
              border: "1px solid rgba(10,179,184,0.25)",
            }}
          />
          <div
            aria-hidden="true"
            className="orbit"
            style={{
              position: "absolute",
              right: -10,
              top: -10,
              width: 160,
              height: 160,
              borderRadius: "50%",
              border: "1px dashed rgba(158,109,242,0.35)",
            }}
          />
          <h3 style={{ fontSize: 30, lineHeight: "36px", maxWidth: 520 }}>{current.title}</h3>
          <p className="body" style={{ maxWidth: 580 }}>
            {current.summary}
          </p>
          <ul className="checks">
            {current.points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          <div className="tint-soft row g8 mt-auto" style={{ padding: "14px 16px", alignItems: "baseline" }}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#a78bfa",
              }}
            >
              How to start
            </span>
            <span style={{ fontSize: 14, lineHeight: "22px", color: "#e2e8f0" }}>{current.start}</span>
          </div>
          <div className="row g12">
            <Link className="btn btn-primary" href={current.ctaHref}>
              {current.cta}
            </Link>
            <Link className="tlink" href={current.href}>
              {current.more} <ArrowRight />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
